/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Add,
  Cancel,
  Close,
  Save,
  CloudUpload,
  Delete,
} from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  FormHelperText,
  Select,
  InputLabel,
  FormControl,
  useMediaQuery,
  Box,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import PageContainer from "@jumbo/components/PageContainer";
import LoadingButton from "@jumbo/components/JumboLoadingButton";
import Button from "@jumbo/components/JumboLoadingButton";
import { istToday, istDateInput } from "app/helpers/istDate";
import { SET_PIECES, isSetProduct } from "app/helpers/setProduct";
import { OFFICE_PURCHASE_ORDERS } from "app/routes/constant";
import {
  createPurchaseOrder,
  getByIdPurchaseOrder,
  updatePurchaseOrder,
  toggleGodownStatus,
} from "app/redux/slice/purchaseOrderSlice";
import { getProductList } from "app/redux/slice/productSlice";
import { getVariantList } from "app/redux/slice/variantSlice";
import {
  createSupplier,
  getSupplierList,
  getVendorRatingColor,
} from "app/redux/slice/supplierSlice";
import { createPartyNumber } from "app/redux/slice/partyNumberSlice";
import { deleteImage, getOrderNo } from "app/redux/slice/invoiceSlice";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { getByIdPrebookOrder } from "app/redux/slice/prebookOrderSlice";
import Loader from "@jumbo/components/Loader";
import DialogDefault from "@jumbo/components/JumboDialog/components/DialogDefault";
import AddVendor from "app/pages/settings/vendor/add-vendor";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import { getUserList } from "app/redux/slice/userSlice";

const useStyles = makeStyles({
  headingTitle: {
    minWidth: "80px",
  },
  table: {
    minWidth: 650,
    "& .MuiTableCell-root": {
      borderLeft: "1px solid",
      borderBottom: "1px solid",
    },
  },
  particular: {
    minWidth: "300px",
  },
  variants: {
    minWidth: "300px",
  },
  amount: {
    minWidth: "100px",
    verticalAlign: "top",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "10px 0px",
  },
  margin: {
    "& > :first-child": {
      marginTop: "10px",
    },
    marginBottom: "10px",
  },
  alignTop: {
    verticalAlign: "top",
  },
  loadingButtonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
  imageUploadContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "5px",
    border: "2px dashed #ccc",
    borderRadius: "8px",
    minHeight: "120px",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      borderColor: "#1976d2",
    },
  },
  imagePreview: {
    width: "80px",
    height: "80px",
    objectFit: "cover",
    borderRadius: "8px",
  },
  hiddenInput: {
    display: "none",
  },
});

// Banding for the product rows. Two tints only, alternating per product block,
// so several rows of the same product read as one group. Kept very pale (~4%
// saturation) because MUI inputs are transparent and sit on top of them - any
// stronger and the field values start to wash out.
const PRODUCT_ROW_TINTS = ["#eff5fc", "#fbf6ef"];
const PRODUCT_GROUP_DIVIDER = "#c3ced9";

// productId -> the order that product first appears in, so the same product
// always gets the same tint even when its rows are not next to each other.
const buildProductBands = (products = []) => {
  const bandByProduct = new Map();
  products.forEach((row) => {
    const productId = row?.productId?._id;
    if (productId && !bandByProduct.has(productId)) {
      bandByProduct.set(productId, bandByProduct.size);
    }
  });
  return bandByProduct;
};

// Formats a stored date for an <input type="date">. Returns "" for a missing
// or unparseable value rather than silently falling back to today: moment(undefined)
// resolves to "now", which is how saved PO dates were being overwritten on edit.
// Formatted in Asia/Kolkata, not the browser's zone - a PC set to a timezone
// ahead of India would otherwise read a stored date back as the next day.
const toDateInput = (value) => istDateInput(value);

const validationSchema = yup.object({
  supplierId: yup.object().required("Please select vendor").nullable(),
  poNumber: yup.string().required("Please enter PO number."),
  poDate: yup
    .string()
    .required("Please enter PO date.")
    // The picker's `max` blocks it too, but a typed value would slip past that.
    .test("not-future", "PO date cannot be in the future.", (value) =>
      value ? value <= istToday() : true
    ),
  deliveryType: yup.string().required("Please select delivery type."),
  // Due Date is deliberately NOT limited - it is meant to be a future date.
  poDueDate: yup.string().required("Please enter PO due date."),
  products: yup.array().of(
    yup.object().shape({
      productId: yup.object().required("Please select product").nullable(),
      variantId: yup.object().required("Please select variant").nullable(),
      quantity: yup
        .number()
        .typeError("Quantity must be a number")
        .required("Please enter quantity")
        .min(1, "Must be greater than or equal to 1"),
      unitPrice: yup
        .number()
        .typeError("Unit price must be a number")
        .required("Please enter unit price")
        .min(1, "Must be greater than or equal to 1"),
      items: yup
        .array()
        .of(
          yup.object().shape({
            weightKg: yup
              .number()
              .typeError("Weight must be a number")
              .min(0, "Weight cannot be negative"),
          }),
        )
        .nullable(),
    }),
  ),
});

const AddAndUpdatePurchaseOrder = () => {
  const { id, orderPreBookId } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isOpen, onClose, onOpen, actionProps } = useDisclosure();

  const supplierData = useSelector((state) => state?.supplier);
  const variantData = useSelector((state) => state?.variant);
  const [isUpdate, setIsUpdate] = useState(false);
  const mobileView = useMediaQuery("(max-width:575px)");
  const [loading, setLoading] = useState(false);
  const [godownToggleLoading, setGodownToggleLoading] = useState({});

  // A product's `partyRate` (which becomes the row's Unit Price) is only
  // returned when the product list is fetched WITH a supplierId - fetched
  // without one, the field is absent and the price silently lands as 0.
  // Since `state.product.list` is shared app-wide, it can still be holding
  // another page's list, or the previous vendor's, while this vendor's fetch
  // is in flight. So the vendor's products are kept in local state here and
  // the Product dropdown stays locked until the list for the *currently
  // selected* vendor has actually arrived.
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [productsLoadedFor, setProductsLoadedFor] = useState(null);
  const latestProductRequestRef = useRef(null);

  // One id per opened form, sent with the create request so the server can tell
  // a resubmission of the same PO apart from a genuinely new one. The atomic PO
  // number stops two saves sharing a number, but not the same form being saved
  // twice under two different numbers. Minted once (useRef, not state) so it
  // survives every re-render and every retry of the same form.
  const requestIdRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `po-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
  );

  const loadProductsForSupplier = (supplierId) => {
    // Marks this as the only response we still care about, so a slower reply
    // for a previously selected vendor can never overwrite a newer one.
    latestProductRequestRef.current = supplierId || null;
    setSupplierProducts([]);
    setProductsLoadedFor(null);

    if (!supplierId) return Promise.resolve([]);

    // Returns the list as well, so callers that prefill rows (pre-book ->  PO)
    // can read this vendor's rates instead of guessing at them.
    return dispatch(getProductList({ field: "name", sort: 1, supplierId }))
      .unwrap()
      .then((response) => {
        if (latestProductRequestRef.current !== supplierId) return [];
        if (response?.status !== 200) {
          enqueueSnackbar(
            response?.data?.responseMessage || "Could not load products",
            { variant: "error" },
          );
          return [];
        }
        const productList = response?.data?.data?.productList ?? [];
        setSupplierProducts(productList);
        setProductsLoadedFor(supplierId);
        return productList;
      });
  };

  let sum = 0;

  const formik = useFormik({
    initialValues: {
      supplierId: null,
      poNumber: "",
      deliveryType: "",
      poDate: istToday(),
      poDueDate: istToday(),
      remark: "",
      products: [
        {
          _id: 1,
          productId: null,
          variantId: null,
          quantity: "",
          unitPrice: 0,
          godownStatus: false,
          productImage: null,
          items: [
            {
              _id: 1,
              item: "",
              weightKg: "",
            },
          ],
        },
      ],
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
      const productArr = [];
      values.products?.forEach((val) => {
        let totalWeight = 0;
        const qty = Number(val.quantity) || 0;
        const items = val?.items?.slice(0, qty).map((item, index) => {
          const weight = +item?.weightKg || 0;
          totalWeight += weight;

          return {
            item: item.item || index + 1,
            weightKg: +item.weightKg || 0,
          };
        });

        productArr.push({
          productId: val?.productId?._id,
          variantId: val?.variantId?._id,
          quantity: val.quantity,
          unitPrice: val.unitPrice,
          godownStatus: val.godownStatus || false,
          totalWeight,
          items,
          productImage: val.productImage, // Add the product image
          imageId: val?.imageId?._id || null,
        });
      });

      // Create FormData for API call
      const formData = new FormData();

      // Add basic form fields
      if (!id) {
        // Identifies THIS filled-in form. If it reaches the server twice the
        // second one is recognised and ignored instead of saving the same PO
        // again under a fresh number. Stays the same across retries on purpose.
        formData.append("requestId", requestIdRef.current);
      }
      if (orderPreBookId) {
        formData.append("orderPreBookId", orderPreBookId);
      }
      formData.append(
        "supplierId",
        values?.supplierId?._id || values?.supplierId,
      );
      formData.append("poNumber", values.poNumber);
      formData.append("poDate", values.poDate);
      formData.append("poDueDate", values.poDueDate);
      formData.append("deliveryType", values.deliveryType);
      formData.append("remark", values.remark);

      // Add products as individual form fields (matching your expected format)
      productArr.forEach((product, productIndex) => {
        formData.append(
          `products[${productIndex}][productId]`,
          product.productId,
        );
        formData.append(
          `products[${productIndex}][totalWeight]`,
          product.totalWeight,
        );
        formData.append(
          `products[${productIndex}][variantId]`,
          product.variantId,
        );
        formData.append(
          `products[${productIndex}][quantity]`,
          product.quantity,
        );
        formData.append(
          `products[${productIndex}][unitPrice]`,
          product.unitPrice,
        );
        formData.append(
          `products[${productIndex}][godownStatus]`,
          product.godownStatus,
        );
        if (id && product?.imageId) {
          formData.append(
            `products[${productIndex}][imageId]`,
            product?.imageId,
          );
        }

        // Add product image if it exists
        if (product.productImage instanceof File) {
          formData.append(
            product.productId, // Use productId as fieldname
            product.productImage,
          );
        }

        product.items?.forEach((item, itemIndex) => {
          formData.append(
            `products[${productIndex}][items][${itemIndex}][item]`,
            item.item,
          );
          formData.append(
            `products[${productIndex}][items][${itemIndex}][weightKg]`,
            item.weightKg,
          );
        });
      });

      try {
        const response = await dispatch(
          id
            ? updatePurchaseOrder({ id, data: formData })
            : createPurchaseOrder(formData),
        ).unwrap();

        if (response.status === 200) {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "success",
          });
          navigate(OFFICE_PURCHASE_ORDERS);
          resetForm();
        } else {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar("An error occurred while processing the request", {
          variant: "error",
        });
      }

      setSubmitting(false);
    },
  });
  useEffect(() => {
    if (!id || orderPreBookId) {
      dispatch(getOrderNo({ type: "PO" }))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const newPoNumber = response?.data?.data?.orderNo;
            setFieldValue("poNumber", newPoNumber);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
    dispatch(getVariantList({ field: "name", sort: 1 }));
    dispatch(getSupplierList({ sort: 1, field: "name", type: "Office" }));
    dispatch(getUserList({ field: "name", sort: 1 }));
  }, []);
  const { values, setFieldValue, errors } = formik;
  const { isSubmitting } = formik;
  const productsLength = values?.products?.length;
  const productBands = buildProductBands(values?.products);

  // True only when the loaded product list belongs to the vendor currently
  // selected in the form - anything else means "still loading" or "stale".
  const productsReady =
    Boolean(values?.supplierId?._id) &&
    productsLoadedFor === values?.supplierId?._id;

  // Handle image upload for specific product - store File object directly
  const handleImageUpload = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        enqueueSnackbar("Please select a valid image file", {
          variant: "error",
        });
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar("Image size should be less than 5MB", {
          variant: "error",
        });
        return;
      }

      // Update the product's image in the products array
      const updatedProducts = [...values.products];
      updatedProducts[index].productImage = file;
      setFieldValue("products", updatedProducts);
    }
  };

  const vendorHandleSubmit = async (data) => {
    const newSupplierData = {
      type: data?.type,
      name: data?.name,
      ...(data?.email && { email: data?.email }),
      address: data?.address,
      brokerName: data?.brokerName,
      mobileNumber: data?.mobileNumber,
      deliveryAddress: data?.deliveryAddress,
      userId: data?.userId,
      gst: data?.gst,
      transportId: data?.transportId,
      transportName: data?.transportName,
      // Kept in step with the same whitelist in pages/settings/vendor/index.js.
      rating: data?.rating ?? null,
    };

    const response = await dispatch(createSupplier(newSupplierData)).unwrap();
    if (response.status === 200) {
      enqueueSnackbar(response.data?.responseMessage, {
        variant: "success",
      });
      if (data.userId && data.mobileNumber) {
        const pnResponse = await dispatch(
          createPartyNumber({
            name: data.name.trim(),
            belongsTo: data.userId,
            city: "",
            phoneNumbers: [data.mobileNumber.trim()],
          })
        ).unwrap();
        if (pnResponse?.status !== 200 && pnResponse?.status !== 201) {
          enqueueSnackbar(
            pnResponse?.data?.responseMessage || "Party number could not be created",
            { variant: "warning" }
          );
        }
      }
      dispatch(getSupplierList({ sort: 1, field: "name", type: "Office" }));
      onClose();
    } else {
      enqueueSnackbar(response.data?.responseMessage, {
        variant: "error",
      });
    }
  };

  const handleImageRemove = (index, imageId, productId) => {
    const updatedProducts = [...values?.products];

    if (updatedProducts[index]) {
      for (let i = 0; i < updatedProducts?.length; i++) {
        const element = updatedProducts[i];
        if (element?.productId?._id === productId?._id) {
          updatedProducts[i] = {
            ...updatedProducts[i],
            productImage: null,
            imageId: null,
          };
        }
      }
      setFieldValue("products", updatedProducts);
    }

    if (imageId) {
      dispatch(
        deleteImage({
          filename: imageId?.image,
          type: "po",
          id: imageId?._id,
        }),
      )
        .unwrap()
        .then((res) => {
          if (res?.status === 200) {
            enqueueSnackbar(res.data?.responseMessage, {
              variant: "success",
            });
          } else {
            enqueueSnackbar(res.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
  };

  // Create object URL for preview (will be cleaned up automatically)
  const getImagePreviewUrl = (file) => {
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    // Handle existing images from API (if they come as URLs)
    if (typeof file === "string") {
      // If it's already a full URL, return as is
      if (file.startsWith("http")) {
        return file;
      }

      // If it's just the image name, prepend the base URL
      return `${process.env.REACT_APP_IMAGE_BASE_URL}po-images/${file}`;
    }
    return null;
  };

  // Check if this is the first occurrence of a product
  const isFirstOccurrenceOfProduct = (currentIndex, productId) => {
    if (!productId) return false;

    for (let i = 0; i < currentIndex; i++) {
      if (values?.products[i]?.productId?._id === productId) {
        return false;
      }
    }
    return true;
  };

  const handleChange = (index, field, value) => {
    let updatedFields = values.products;
    updatedFields[index][field] = value;
    // If productId is changed, set unitPrice from partyRate
    if (field === "productId" && value) {
      updatedFields[index].unitPrice = value.partyRate || 0;
      // A set product always goes out as a set of 6 pieces, so the row opens
      // with Qty 6 - and with it the 6 weight rows to fill in. Still editable
      // (e.g. 12 for two sets). Other products are left exactly as before.
      if (isSetProduct(value)) {
        updatedFields[index].quantity = SET_PIECES;
      }
    }
    if (field === "items") {
      updatedFields[index].items = value;
    }
    setFieldValue("products", updatedFields);
  };

  const handleAddMore = () => {
    formik.setValues({
      ...formik.values,
      products: [
        ...formik.values.products,
        {
          productId: null,
          variantId: null,
          quantity: "",
          godownStatus: false,
          items: [
            {
              item: "",
              weightKg: "",
            },
          ],
        },
      ],
    });
  };

  const handleAddMoreColor = () => {
    const lastProduct =
      values?.products[values?.products?.length - 1]?.productId || null;
    formik.setValues({
      ...formik.values,
      products: [
        ...formik.values.products,
        {
          productId: lastProduct,
          variantId: null,
          unitPrice:
            values?.products[values?.products?.length - 1]?.unitPrice || 0,
          // Same product as the row above, so a set product starts at 6 here too.
          quantity: isSetProduct(lastProduct) ? SET_PIECES : "",
          godownStatus: false,
          items: [
            {
              item: "",
              weightKg: "",
            },
          ],
        },
      ],
    });
  };

  const handleDeleteField = (index) => {
    const productToDelete = values?.products[index];
    let updatedFields = [...values?.products];
    updatedFields.splice(index, 1);

    // If we deleted a product and it was the first occurrence,
    // check if there are other instances of the same product
    if (productToDelete?.productId?._id) {
      const productId = productToDelete?.productId?._id;
      const hasOtherInstances = updatedFields.some(
        (product) => product?.productId?._id === productId,
      );

      if (!hasOtherInstances) {
        // If no other instances exist, remove the image
        handleImageRemove(index, null, null);
      } else {
        // If there are other instances, find the first remaining instance and update its image
        const firstRemainingIndex = updatedFields?.findIndex(
          (product) => product?.productId?._id === productId,
        );
        if (firstRemainingIndex !== -1) {
          updatedFields[firstRemainingIndex] = {
            ...updatedFields[firstRemainingIndex],
            productImage: productToDelete?.productImage,
            imageId: productToDelete?.imageId,
          };
        }
      }
    }

    // Update the form with the new products array
    setFieldValue("products", updatedFields);

    // If no products left, clear all images
    if (updatedFields.length === 0) {
      setFieldValue("productImages", {});
    }
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      dispatch(getByIdPurchaseOrder(id))
        .unwrap()
        .then((response) => {
          if (response?.status === 200) {
            const purchaseOrderDetail =
              response?.data?.data?.purchaseOrderDetail;
            let productArr = [];
            loadProductsForSupplier(purchaseOrderDetail?.supplierId?._id);
            setFieldValue("supplierId", purchaseOrderDetail?.supplierId);
            // The PO stores poDate / poDueDate. Reading a non-existent `date`
            // handed moment(undefined), which silently resolves to "now" - so
            // opening an old PO for edit showed today's date, and saving would
            // write it over the real one. poDueDate was also being filled from
            // the same wrong field, so it never loaded its own value.
            setFieldValue("poDate", toDateInput(purchaseOrderDetail?.poDate));
            setFieldValue(
              "poDueDate",
              toDateInput(purchaseOrderDetail?.poDueDate),
            );
            setFieldValue("remark", purchaseOrderDetail?.remark);
            setFieldValue("poNumber", purchaseOrderDetail?.poNumber);
            setFieldValue("deliveryType", purchaseOrderDetail?.deliveryType);

            // Load existing images if available (these will be URLs from server)
            if (purchaseOrderDetail?.productImages) {
              setFieldValue("productImages", purchaseOrderDetail.productImages);
            }

            purchaseOrderDetail?.products?.forEach((val) => {
              const items = val.items?.map((item, index) => ({
                item: item.item || `${index + 1}`,
                weightKg: item.weightKg || "",
              }));
              productArr.push({
                productId: val?.productId,
                variantId: val?.variantId,
                quantity: val?.quantity,
                unitPrice: val?.unitPrice || 0,
                godownStatus: val?.godownStatus || false,
                items: items || [],
                productImage: val?.imageId?.imageUrl || null,
                imageId: val?.imageId || null,
              });
            });
            setFieldValue("products", productArr);
            setIsUpdate(
              purchaseOrderDetail?.isInvoiceCreated === true ? false : true,
            );
            setLoading(false);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
            setLoading(false);
          }
        });
    }
    if (orderPreBookId) {
      setLoading(true);
      dispatch(getByIdPrebookOrder(orderPreBookId))
        .unwrap()
        .then(async (response) => {
          if (response.status === 200) {
            const prebookDetail = response?.data?.data?.prebookDetail;
            // The vendor-wise rate lives on the vendor's own product list
            // (`partyRate`), not on the product the pre-book stored - so wait
            // for that list and carry the rate over, exactly as picking the
            // product by hand does. Without this the rows landed with no
            // unitPrice at all and every converted PO started at 0.
            const vendorProducts = await loadProductsForSupplier(
              prebookDetail?.supplierId?._id,
            );
            // Carries the vendor's `rating` too - the Vendor box is coloured
            // from it, so a copy without it left the box uncoloured while the
            // dropdown showed the colour.
            setFieldValue("supplierId", prebookDetail?.supplierId);
            // Pre-book genuinely stores `date`. Falling back to today is fine
            // here because this path is creating a brand new PO.
            setFieldValue(
              "poDate",
              toDateInput(prebookDetail?.date) || istToday(),
            );
            // Carries over what the Supplier chose in the App instead of
            // leaving Admin to re-pick it. Still an ordinary form value, so it
            // can be changed here like any other.
            if (prebookDetail?.deliveryType) {
              setFieldValue("deliveryType", prebookDetail.deliveryType);
            }

            let missingFromVendor = 0;
            const productArr = (prebookDetail?.products ?? []).map((val) => {
              const vendorProduct = vendorProducts?.find(
                (product) => product?._id === val?.productId?._id,
              );
              // A vendor whose price list is missing the product, or carries it
              // at 0, would otherwise land the row at ₹0. Fall back to the rate
              // the Supplier was actually quoted on the pre-order.
              const rate =
                Number(vendorProduct?.partyRate) || Number(val?.unitPrice) || 0;
              if (!rate) missingFromVendor += 1;

              return {
                // Prefer the vendor's own copy so the row carries partyRate and
                // matches an option in the Product dropdown.
                productId: vendorProduct ?? val?.productId,
                variantId: val?.variantId,
                quantity: val?.quantity,
                unitPrice: rate,
                // Weights are deliberately NOT carried over: what the Supplier
                // entered in the App is only their ~25 KG estimate, and every
                // roll gets weighed here instead.
                items: [],
              };
            });
            setFieldValue("products", productArr);

            if (missingFromVendor) {
              enqueueSnackbar(
                `${missingFromVendor} product(s) have no price for this vendor - please set their price manually.`,
                { variant: "warning" },
              );
            }
            setLoading(false);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
            setLoading(false);
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id, setFieldValue]);

  const { handleSubmit } = formik;

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <PageContainer
          heading={id ? "Update Purchase Order" : "Add Purchase Order"}
        >
          <Card>
            <CardContent>
              <FormikProvider value={formik}>
                {/* A save already in flight must not be started again. The Save
                    button disables itself, but Enter inside any field submits
                    the form directly and bypasses it - one of the ways the same
                    PO got saved twice. */}
                <Form
                  noValidate
                  autoComplete="off"
                  onSubmit={(event) => {
                    if (isSubmitting) {
                      event.preventDefault();
                      return;
                    }
                    handleSubmit(event);
                  }}
                >
                  <Grid container border={1}>
                    <Grid item sm={3} xs={12} borderRight={{ xs: 0, sm: 1 }}>
                      <Grid>
                        <Grid px={1} pt={1} mb={1}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <JumboAutoComplete
                                options={supplierData.list ?? []}
                                label="Vendor*"
                                size="small"
                                getOptionLabel={(option) => option?.name}
                                name={"supplierId"}
                                value={values?.supplierId}
                                onChange={(evt, value) => {
                                  setFieldValue("supplierId", value);
                                  loadProductsForSupplier(value?._id);
                                  setFieldValue("products", [
                                    {
                                      _id: 1,
                                      productId: null,
                                      variantId: null,
                                      quantity: "",
                                      unitPrice: 0,
                                      godownStatus: false,
                                      productImage: null,
                                      items: [
                                        {
                                          _id: 1,
                                          item: "",
                                          weightKg: "",
                                        },
                                      ],
                                    },
                                  ]);
                                }}
                                helperText={formik?.errors?.supplierId}
                                isOptionEqualToValue={(option, value) =>
                                  option?._id === value?._id
                                }
                                // renderOption and sx pass through
                                // JumboAutoComplete's {...rest}; renderInput is
                                // hardcoded there so the selected value has to
                                // be coloured via the input's class.
                                renderOption={(props, option) => (
                                  <li
                                    {...props}
                                    key={option?._id}
                                    style={{
                                      color: getVendorRatingColor(
                                        option?.rating,
                                      ),
                                    }}
                                  >
                                    {option?.name}
                                  </li>
                                )}
                                sx={{
                                  "& .MuiAutocomplete-input": {
                                    color: getVendorRatingColor(
                                      values?.supplierId?.rating,
                                    ),
                                  },
                                }}
                              />
                            </Box>
                            <Tooltip title="Add Vendor">
                              <IconButton
                                color="primary"
                                size="small"
                                aria-label="add vendor"
                                onClick={() => {
                                  onOpen();
                                }}
                                sx={{ padding: "6px" }}
                              >
                                <Add />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item sm={9} xs={12}>
                      <Grid container>
                        <Grid
                          item
                          sm={3}
                          xs={12}
                          borderRight={{ xs: 0, sm: 1 }}
                          p={1}
                        >
                          <TextField
                            disabled
                            label={"PO No."}
                            name="poNumber"
                            fullWidth
                            size="small"
                            value={values?.poNumber}
                            onChange={(evt) =>
                              setFieldValue("poNumber", evt.target.value)
                            }
                            error={errors?.poNumber}
                            helperText={errors?.poNumber}
                          />
                        </Grid>
                        <Grid
                          item
                          sm={3}
                          xs={12}
                          borderRight={{ xs: 0, sm: 1 }}
                          p={1}
                        >
                          <TextField
                            label={"Date"}
                            name="poDate"
                            fullWidth
                            size="small"
                            type="date"
                            onChange={(evt) =>
                              setFieldValue("poDate", evt.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            // Today in India - stops a future date being picked.
                            inputProps={{ max: istToday() }}
                            value={values?.poDate}
                            error={errors?.poDate}
                            helperText={errors?.poDate}
                          />
                        </Grid>
                        <Grid
                          item
                          sm={3}
                          xs={12}
                          borderRight={{ xs: 0, sm: 1 }}
                          p={1}
                        >
                          <TextField
                            label={"Due Date"}
                            name="poDueDate"
                            fullWidth
                            size="small"
                            type="date"
                            onChange={(evt) =>
                              setFieldValue("poDueDate", evt.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            value={values?.poDueDate}
                            error={errors?.poDueDate}
                            helperText={errors?.poDueDate}
                          />
                        </Grid>
                        <Grid item sm={3} xs={12} p={1}>
                          <FormControl
                            fullWidth
                            size="small"
                            error={!!formik?.errors?.deliveryType}
                          >
                            <InputLabel>Delivery Type*</InputLabel>
                            <Select
                              name={`deliveryType`}
                              value={values?.deliveryType || ""}
                              onChange={(evt) =>
                                setFieldValue("deliveryType", evt.target.value)
                              }
                            >
                              {[
                                "Direct Parcel",
                                "Direct Delivery",
                                "Payment After Parcel",
                                "Payment After Delivery",
                                "Sample",
                              ].map((val) => {
                                return (
                                  <MenuItem key={val} value={val}>
                                    {val}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                            <FormHelperText>
                              {formik?.errors?.deliveryType}
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    borderRight={1}
                    sx={{
                      mt: { xs: 3, md: 0 },
                    }}
                    className="mobile-view-table"
                  >
                    <TableContainer component={Paper}>
                      <Table
                        sx={{ minWidth: 650 }}
                        size="small"
                        aria-label="a dense table"
                        className={`${classes?.table}`}
                      >
                        {!mobileView ? (
                          <TableHead>
                            <TableRow>
                              <TableCell>Sr. No.</TableCell>
                              <TableCell className={classes?.particular}>
                                Product
                              </TableCell>
                              <TableCell className={classes?.variants}>
                                Variant
                              </TableCell>
                              <TableCell style={{ minWidth: "100px" }}>
                                Qty
                              </TableCell>
                              <TableCell>Item</TableCell>
                              <TableCell>Weight (KG)</TableCell>
                              <TableCell>Unit Price</TableCell>
                              <TableCell>Product Image</TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          </TableHead>
                        ) : (
                          ""
                        )}

                        <TableBody>
                          {productsLength &&
                            values?.products?.map((item, index) => {
                              sum += item?.amount ? +item?.amount : 0;

                              const productId = item?.productId?._id;
                              const band = productBands.get(productId);
                              // A row whose product is not picked yet stays
                              // plain - there is nothing to group it with.
                              const rowTint =
                                band === undefined
                                  ? undefined
                                  : PRODUCT_ROW_TINTS[
                                      band % PRODUCT_ROW_TINTS.length
                                    ];
                              // Only the first row of a block is capped, so a
                              // block is still readable as one unit when two
                              // neighbouring blocks land on the same tint.
                              const startsBlock =
                                index > 0 &&
                                values?.products?.[index - 1]?.productId?._id !==
                                  productId;

                              return (
                                <TableRow
                                  key={index}
                                  sx={{
                                    backgroundColor: rowTint,
                                    ...(startsBlock && {
                                      "& > .MuiTableCell-root": {
                                        borderTop: `2px solid ${PRODUCT_GROUP_DIVIDER}`,
                                      },
                                    }),
                                  }}
                                >
                                  {!mobileView ? (
                                    <TableCell
                                      className={classes?.alignTop}
                                      sx={{ width: "10px", paddingTop: "20px" }}
                                    >
                                      {index + 1}
                                    </TableCell>
                                  ) : (
                                    ""
                                  )}
                                  <TableCell className={classes?.alignTop}>
                                    <JumboAutoComplete
                                      sx={{ marginTop: "7px" }}
                                      // Empty until this vendor's list is in,
                                      // so a stale product (whose partyRate
                                      // would be missing) can never be picked.
                                      options={
                                        productsReady ? supplierProducts : []
                                      }
                                      disabled={!productsReady}
                                      label={
                                        !values?.supplierId
                                          ? "Select Vendor First"
                                          : productsReady
                                          ? "Product*"
                                          : "Loading Products..."
                                      }
                                      size="small"
                                      getOptionLabel={(option) => option?.name}
                                      name={`products[${index}].productId`}
                                      value={item?.productId}
                                      onChange={(evt, value) => {
                                        handleChange(index, "productId", value);
                                        handleChange(index, "variantId", null);
                                      }}
                                      helperText={
                                        formik?.errors?.products?.length &&
                                        formik?.errors?.products[index]
                                          ?.productId
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                      }
                                    />
                                  </TableCell>
                                  <TableCell className={classes?.alignTop}>
                                    <JumboAutoComplete
                                      options={
                                        item?.productId
                                          ? variantData?.list.filter(
                                              (list) =>
                                                list?.product?._id ===
                                                item?.productId?._id,
                                            ) || []
                                          : []
                                      }
                                      sx={{ marginTop: "7px" }}
                                      label="Variant*"
                                      size="small"
                                      getOptionLabel={(option) => option?.name}
                                      name={`products[${index}].variantId`}
                                      value={item?.variantId}
                                      onChange={(evt, value) =>
                                        handleChange(index, "variantId", value)
                                      }
                                      helperText={
                                        formik?.errors?.products?.length &&
                                        formik?.errors?.products[index]
                                          ?.variantId
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                      }
                                    />
                                  </TableCell>

                                  <TableCell className={`${classes?.alignTop}`}>
                                    <TextField
                                      sx={{ marginTop: "7px" }}
                                      placeholder="Qty"
                                      label="Qty"
                                      name={`products[${index}].quantity`}
                                      fullWidth
                                      value={item?.quantity}
                                      size="small"
                                      type="number"
                                      onWheel={(e) => e.target.blur()} // ✅ scroll disable
                                      onChange={(evt) =>
                                        handleChange(
                                          index,
                                          "quantity",
                                          +evt.target.value,
                                        )
                                      }
                                      error={
                                        errors?.products?.length &&
                                        errors?.products?.[index]?.quantity
                                      }
                                      helperText={
                                        (errors?.products?.length &&
                                          errors?.products?.[index]?.quantity) ||
                                        (isSetProduct(item?.productId)
                                          ? `1 set = ${SET_PIECES} pcs`
                                          : "")
                                      }
                                    />
                                    <FormControlLabel
                                      sx={{ mt: 1, ml: 0 }}
                                      control={
                                        godownToggleLoading[index] ? (
                                          <CircularProgress
                                            size={20}
                                            sx={{ mr: 1 }}
                                          />
                                        ) : (
                                          <Switch
                                            size="small"
                                            checked={
                                              item?.godownStatus || false
                                            }
                                            disabled={
                                              godownToggleLoading[index]
                                            }
                                            onChange={() => {
                                              if (id) {
                                                // Existing order — call API
                                                setGodownToggleLoading(
                                                  (prev) => ({
                                                    ...prev,
                                                    [index]: true,
                                                  }),
                                                );
                                                dispatch(
                                                  toggleGodownStatus({
                                                    id,
                                                    productIndex: index,
                                                  }),
                                                )
                                                  .unwrap()
                                                  .then((res) => {
                                                    if (res?.status === 200) {
                                                      enqueueSnackbar(
                                                        res.data
                                                          ?.responseMessage,
                                                        { variant: "success" },
                                                      );
                                                      const updatedProducts = [
                                                        ...values.products,
                                                      ];
                                                      updatedProducts[index] = {
                                                        ...updatedProducts[
                                                          index
                                                        ],
                                                        godownStatus:
                                                          res.data?.data
                                                            ?.godownStatus,
                                                      };
                                                      setFieldValue(
                                                        "products",
                                                        updatedProducts,
                                                      );
                                                    } else {
                                                      enqueueSnackbar(
                                                        res.data
                                                          ?.responseMessage ||
                                                          "Failed to update godown status",
                                                        { variant: "error" },
                                                      );
                                                    }
                                                  })
                                                  .finally(() => {
                                                    setGodownToggleLoading(
                                                      (prev) => ({
                                                        ...prev,
                                                        [index]: false,
                                                      }),
                                                    );
                                                  });
                                              } else {
                                                // New order — just toggle local state
                                                handleChange(
                                                  index,
                                                  "godownStatus",
                                                  !item?.godownStatus,
                                                );
                                              }
                                            }}
                                          />
                                        )
                                      }
                                      label={
                                        <Box
                                          component="span"
                                          sx={{
                                            fontSize: "12px",
                                            color: item?.godownStatus
                                              ? "green"
                                              : "text.secondary",
                                            fontWeight: 500,
                                          }}
                                        >
                                          godown
                                        </Box>
                                      }
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      minWidth: "100px",
                                      maxWidth: "100px",
                                    }}
                                    className={`${classes?.alignTop} items-td`}
                                  >
                                    {Array.from(
                                      { length: item?.quantity },
                                      (_, i) => {
                                        return (
                                          <TextField
                                            key={i}
                                            sx={{ marginTop: "7px" }}
                                            disabled
                                            fullWidth
                                            label="Item"
                                            placeholder="Item"
                                            name={`products[${index}].items[${i}].item`}
                                            value={
                                              item?.items[i]?.item || i + 1
                                            }
                                            size="small"
                                            onChange={(evt) => {
                                              const updatedItems = [
                                                ...item.items,
                                              ];
                                              updatedItems[i] = {
                                                ...updatedItems[i],
                                                item: evt.target.value,
                                              };
                                            }}
                                            error={
                                              formik?.errors?.products
                                                ?.length &&
                                              formik?.errors?.products?.[index]
                                                ?.items?.[i]?.item
                                            }
                                            helperText={
                                              formik?.errors?.products
                                                ?.length &&
                                              formik?.errors?.products?.[index]
                                                ?.items?.[i]?.item
                                            }
                                          />
                                        );
                                      },
                                    )}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      minWidth: "150px",
                                      maxWidth: "150px",
                                    }}
                                    className={`${classes?.alignTop} weight-td`}
                                  >
                                    {Array.from(
                                      { length: item.quantity },
                                      (_, i) => {
                                        return (
                                          <TextField
                                            key={i}
                                            sx={{ marginTop: "7px" }}
                                            type="number"
                                            onWheel={(e) => e.target.blur()} // ✅ scroll disable
                                            placeholder="Weight"
                                            label="Weight"
                                            name={`products[${index}].items[${i}].weightKg`}
                                            fullWidth
                                            value={
                                              item?.items[i]?.weightKg || ""
                                            }
                                            size="small"
                                            onChange={(evt) => {
                                              const updatedItems = [
                                                ...item.items,
                                              ];
                                              updatedItems[i] = {
                                                ...updatedItems[i],
                                                weightKg: evt.target.value,
                                              };
                                              handleChange(
                                                index,
                                                "items",
                                                updatedItems,
                                              );
                                            }}
                                            error={
                                              formik?.errors?.products
                                                ?.length &&
                                              formik?.errors?.products?.[index]
                                                ?.items?.[i]?.weightKg
                                            }
                                            helperText={
                                              formik?.errors?.products
                                                ?.length &&
                                              formik?.errors?.products?.[index]
                                                ?.items?.[i]?.weightKg
                                            }
                                          />
                                        );
                                      },
                                    )}
                                  </TableCell>

                                  {/* Unit Price Column */}
                                  <TableCell
                                    className={classes?.alignTop}
                                    sx={{
                                      minWidth: "100px",
                                      maxWidth: "100px",
                                    }}
                                  >
                                    <TextField
                                      sx={{ marginTop: "7px" }}
                                      label="Price"
                                      name={`products[${index}].unitPrice`}
                                      fullWidth
                                      value={item?.unitPrice || 0}
                                      size="small"
                                      type="text"
                                      disabled
                                      error={
                                        errors?.products?.length &&
                                        errors?.products?.[index]?.unitPrice
                                      }
                                      helperText={
                                        errors?.products?.length &&
                                        errors?.products?.[index]?.unitPrice
                                      }
                                    />
                                  </TableCell>
                                  {/* Image Upload Column - Show only for first occurrence of each product */}
                                  <TableCell
                                    className={classes?.alignTop}
                                    sx={{
                                      minWidth: "150px",
                                      maxWidth: "150px",
                                    }}
                                  >
                                    {item?.productId &&
                                      isFirstOccurrenceOfProduct(
                                        index,
                                        item?.productId?._id,
                                      ) && (
                                        <Box sx={{ marginTop: "7px" }}>
                                          <input
                                            accept="image/*"
                                            className={classes.hiddenInput}
                                            id={`image-upload-${index}`}
                                            type="file"
                                            onChange={(e) =>
                                              handleImageUpload(e, index)
                                            }
                                          />
                                          <label
                                            htmlFor={`image-upload-${index}`}
                                          >
                                            <Box
                                              className={
                                                classes.imageUploadContainer
                                              }
                                            >
                                              {item?.productImage ? (
                                                <Box
                                                  sx={{
                                                    position: "relative",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  <img
                                                    src={getImagePreviewUrl(
                                                      item?.productImage,
                                                    )}
                                                    alt="Product Preview"
                                                    className={
                                                      classes?.imagePreview
                                                    }
                                                    onLoad={(e) => {
                                                      if (
                                                        e.target.src.startsWith(
                                                          "blob:",
                                                        )
                                                      ) {
                                                        // Object URL will be cleaned up by browser
                                                      }
                                                    }}
                                                  />
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      handleImageRemove(
                                                        index,
                                                        item?.imageId?._id
                                                          ? item?.imageId
                                                          : null,
                                                        item?.productId,
                                                      );
                                                    }}
                                                    sx={{
                                                      position: "absolute",
                                                      top: -8,
                                                      right: -8,
                                                      backgroundColor: "white",
                                                      "&:hover": {
                                                        backgroundColor:
                                                          "#f5f5f5",
                                                      },
                                                    }}
                                                  >
                                                    <Delete
                                                      fontSize="small"
                                                      color="error"
                                                    />
                                                  </IconButton>
                                                </Box>
                                              ) : (
                                                <>
                                                  <CloudUpload color="action" />
                                                  <Box
                                                    sx={{
                                                      fontSize: "12px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Click to upload
                                                    <br />
                                                    {item?.productId?.name ||
                                                      "product"}{" "}
                                                    image
                                                  </Box>
                                                </>
                                              )}
                                            </Box>
                                          </label>
                                        </Box>
                                      )}
                                    {(!item?.productId ||
                                      !isFirstOccurrenceOfProduct(
                                        index,
                                        item?.productId?._id,
                                      )) && (
                                      <Box
                                        sx={{
                                          marginTop: "7px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          minHeight: "120px",
                                          color: "#ccc",
                                          fontSize: "12px",
                                          textAlign: "center",
                                        }}
                                      >
                                        {item?.productId &&
                                        values?.products?.find(
                                          (p) =>
                                            p?.productId?._id ===
                                            item?.productId?._id,
                                        )?.productImage ? (
                                          <Box sx={{ textAlign: "center" }}>
                                            <img
                                              src={getImagePreviewUrl(
                                                values?.products?.find(
                                                  (p) =>
                                                    p?.productId?._id ===
                                                    item?.productId?._id,
                                                )?.productImage,
                                              )}
                                              alt="Product Preview"
                                              className={classes.imagePreview}
                                            />
                                            <Box
                                              sx={{ fontSize: "10px", mt: 1 }}
                                            >
                                              (Same as above)
                                            </Box>
                                          </Box>
                                        ) : item?.productId ? (
                                          "Same product as above"
                                        ) : (
                                          "Select product first"
                                        )}
                                      </Box>
                                    )}
                                  </TableCell>

                                  <TableCell sx={{ width: "10px" }}>
                                    {values?.products?.length > 1 && (
                                      <IconButton
                                        aria-label="delete"
                                        color="secondary"
                                        onClick={() => handleDeleteField(index)}
                                      >
                                        <Cancel />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                          <TableRow>
                            <TableCell
                              colSpan={6}
                              style={{ borderBottom: "0", borderRight: "0" }}
                            >
                              <TextField
                                label={"Remark"}
                                name="remark"
                                fullWidth
                                size="small"
                                value={values?.remark}
                                onChange={(evt) =>
                                  setFieldValue("remark", evt.target.value)
                                }
                              />
                            </TableCell>
                            <TableCell colSpan={3}>
                              <Grid className={classes?.buttonContainer}>
                                <Button
                                  style={{ minWidth: "160px" }}
                                  type="button"
                                  buttonLabel={"Add Color"}
                                  startIcon={<Add />}
                                  variant="outlined"
                                  className="add-btn"
                                  onClick={handleAddMoreColor}
                                />
                              </Grid>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={6}></TableCell>
                            <TableCell colSpan={3}>
                              <Grid className={classes?.buttonContainer}>
                                <Button
                                  style={{ minWidth: "160px" }}
                                  type="button"
                                  buttonLabel={"Add Product"}
                                  startIcon={<Add />}
                                  variant="outlined"
                                  onClick={handleAddMore}
                                  className="add-btn"
                                />
                              </Grid>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      sm={12}
                      xs={12}
                      className={classes?.loadingButtonContainer}
                    >
                      <LoadingButton
                        disabled={id && !isUpdate}
                        type="submit"
                        buttonLabel={id ? "Update" : "Save"}
                        sx={{ marginRight: "10px" }}
                        startIcon={<Save />}
                        variant="outlined"
                        fullWidth={mobileView}
                        loading={isSubmitting}
                      />
                      <LoadingButton
                        color={"secondary"}
                        buttonLabel={"Close"}
                        variant="outlined"
                        startIcon={<Close />}
                        onClick={() => navigate(OFFICE_PURCHASE_ORDERS)}
                        fullWidth={mobileView}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </FormikProvider>
            </CardContent>
          </Card>
          <DialogDefault
            open={isOpen}
            onClose={onClose}
            content={
              <AddVendor
                value={actionProps}
                onClose={onClose}
                handleSubmit={vendorHandleSubmit}
              />
            }
            title={"Add Vendor"}
            fullWidth={true}
            maxWidth={"md"}
            actions={<></>}
          />
        </PageContainer>
      )}
    </>
  );
};

export default AddAndUpdatePurchaseOrder;
